/**
 * Utility for working with route configurations from adminPreferences.json
 * This helps avoid hardcoding routes throughout the application
 */

// Central single-point route helpers
// -----------------------------------------------------------------------------
// WARNING: This file is intentionally lightweight. **Do not** add business
// logic here. The single source of truth for paths lives in
// `app/config/adminPreferences.json` and the generated helpers in
// `app/utils/pz/routes.generated.ts`. Regenerate helpers with:
//   pnpm build:routes
// -----------------------------------------------------------------------------

import { applyCollectionEntityToPath } from '#app/utils/pz/brand/collectionEntity'
import { applyMainEntityToPath } from '#app/utils/pz/brand/mainEntity'
import {
	getPublicCatalogIndexPath,
	getPublicProgramDetailPath,
	getPublicProgramsIndexPath,
} from '#app/utils/pz/publicLearningRoutes'
import {
	ROUTE_TREE,
	routeMap,
	path,
	build,
	type RouteKey,
} from './routes.generated'

// Re-export for external consumers ------------------------------------------------
export { ROUTE_TREE, routeMap, path, build }
export type { RouteKey }

// Legacy aliases (scheduled for removal) -----------------------------------------
export const getRoutePath = path
export const generateRoute = build

// Convenience wrappers -----------------------------------------------------------
export const courseRoutes = {
	base: () => applyMainEntityToPath(path('courses.base')),
	list: () => path('studyPanel.base'),
	detail: (courseId: string) =>
		applyMainEntityToPath(build('courses.detail', { courseId })),
	diagram: (courseId: string) =>
		`${applyMainEntityToPath(build('courses.detail', { courseId }))}/diagram`,
	cards: (courseId: string) =>
		`${applyMainEntityToPath(build('courses.detail', { courseId }))}/cards`,
	// Template-Instance specific routes
	topics: {
		list: (courseId: string) =>
			applyMainEntityToPath(build('topics.base', { courseId })),
		detail: (courseId: string, topicId: string) =>
			applyMainEntityToPath(build('topics.detail', { courseId, topicId })),
		resources: {
			list: (courseId: string, topicId: string) =>
				applyMainEntityToPath(
					build('topics.resources.base', { courseId, topicId }),
				),
			detail: (courseId: string, topicId: string, resourceId: string) =>
				applyMainEntityToPath(
					build('topics.resources.detail', {
						courseId,
						topicId,
						resourceId,
					}),
				),
		},
	},
}

export const collectionRoutes = {
	base: () => applyCollectionEntityToPath(path('collections.base')),
	list: () => applyCollectionEntityToPath(path('collections.base')),
	detail: (collectionId: string) =>
		applyCollectionEntityToPath(
			build('collections.detail', { collectionSlug: collectionId }),
		),
}

export const dashboardRoutes = {
	base: () => path('dashboard'),
	// There is no generated key for bookmarks in routes.generated.ts.
	// Compose from the dashboard base path to ensure stability.
	bookmarks: () => `${path('dashboard')}/my-bookmarks`,
}

export const publicRoutes = {
	home: () => path('home'),
	docs: () => path('docs.base'),
	legal: () => path('legal'),
	blog: () => path('blog'),
	about: () => path('about'),
	contact: () => path('contact'),
	courses: {
		list: () => applyMainEntityToPath(path('marketing.courses')),
		collectionsList: () => getPublicProgramsIndexPath(),
		detail: (courseSlug: string) =>
			applyMainEntityToPath(build('marketing.courseDetail', { courseSlug })),
		preview: (courseSlug: string) =>
			applyMainEntityToPath(build('marketing.coursePreview', { courseSlug })),
	},
	programs: {
		list: () => getPublicProgramsIndexPath(),
		detail: (programSlug: string) => getPublicProgramDetailPath(programSlug),
	},
	catalog: {
		index: () => getPublicCatalogIndexPath(),
	},
}

export const internalRoutes = {
	docs: () => path('internalDocs.base'),
}
